import currentTimeZone from "~/kernel/dateTime/currentTimeZone";
import buildParams from "~/kernel/searchParams/buildParams";
import type { Company, InvoiceDiscount } from "@prisma/client";
import route from "./route";
import type ImportType from "~/routes/admin.companies.$id.importers/ImportType";
import format from "~/kernel/dateTime/format";
import endOf from "~/kernel/dateTime/endOf";
import startOf from "~/kernel/dateTime/startOf";
import { z } from "zod";
import DateFormat from "../dateTime/DateFormat";
import { SubscriptionInvoiceStatus } from "~/invoicing/models/SubscriptionInvoiceStatus";
import i18n from "~/i18n";
import { LOCALES } from "~/kernel/internationalization/Locale";

const SUBSCRIPTION_ROUTES = {
  cancelSubscription: route(
    ({ companyId }: { companyId: string }) =>
      `/companies/${companyId}/subscription/cancel`,
  ),
};

const ANALYTICS_DATE_RANGE_QUERY = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
});

const ANALYTICS_ROUTES = {
  analytics: route.fromTemplate("/admin/analytics"),
  bookingAnalytics: route.fromTemplate("/admin/booking-analytics", {
    query: ANALYTICS_DATE_RANGE_QUERY.extend({
      companyId: z.string().optional(),
    }),
  }),
  passwordResetAnalytics: route.fromTemplate(
    "/admin/password-reset-analytics",
    { query: ANALYTICS_DATE_RANGE_QUERY },
  ),
};

const JOB_ROUTES = {
  scheduledJobs: route(
    ({ page = 1 } = {}) => `/admin/scheduled-jobs${buildParams({ page })}`,
  ),
};

const REVIEW_IMPORT_ROUTES = {
  newReviewImport: route.fromTemplate(
    "/admin/companies/:id/review-imports/new",
    {
      params: z.object({ id: z.string() }),
    },
  ),
};

const INVOICE_ROUTES = {
  attestInvoices: route.fromTemplate("/admin/invoices/attest", {
    query: z.object({
      status: z.nativeEnum(SubscriptionInvoiceStatus).optional(),
      year: z.number().optional(),
      month: z.number().optional(),
    }),
  }),
  generateInvoices: route.fromTemplate("/admin/subscription-invoices/generate"),
  activateDiscount: route(
    ({ id }: Pick<InvoiceDiscount, "id">) =>
      `/admin/invoice-discounts/${id}/activate`,
  ),
  deactivateDiscount: route(
    ({ id }: { id: string }) => `/admin/invoice-discounts/${id}/deactivate`,
  ),
  editSubscriptionInvoice: route.fromTemplate(
    "/admin/subscription-invoices/:id/edit",
    {
      params: z.object({ id: z.string() }),
    },
  ),
  editDiscount: route(
    ({ id }: Pick<InvoiceDiscount, "id">) =>
      `/admin/invoice-discounts/${id}/edit`,
  ),
  manageDiscounts: route("/admin/invoice-discounts?index"),
  newDiscount: route("/admin/invoice-discounts/new"),
  newCustomInvoice: route("/admin/custom-invoices/new"),
  newSubscriptionInvoice: route(
    ({ companyId }: { companyId?: string }) =>
      `/admin/subscription-invoices/new${buildParams({ companyId })}`,
  ),
  subscriptionInvoiceProcessingPayment: route.fromTemplate(
    "/admin/subscription-invoices/:id/processing-payment",
    {
      params: z.object({ id: z.string() }),
    },
  ),
};

const COMPANY_ROUTES = {
  editSmsAccount: route.fromTemplate("/admin/companies/:id/sms-account/edit", {
    params: z.object({ id: z.string() }),
  }),
  editCompanies: route("/admin/companies"),
  editCompany: route(({ id }: { id: string }) => `/admin/companies/${id}/edit`),
  geocodeCompany: route(
    ({ id }: { id: string }) => `/admin/companies/${id}/geocode`,
  ),
  geocodeCompanies: route("/admin/companies/geocode"),
  editCompanyCoordinates: route.fromTemplate(
    "/admin/companies/:id/coordinates/edit",
    { params: z.object({ id: z.string() }) },
  ),
  companyUsage: route(
    ({ id, from, to }: Pick<Company, "id"> & { from: Date; to: Date }) => {
      const fromStr = format(
        startOf(from, "month", { timeZone: currentTimeZone() }),
        DateFormat.Date46Elks,
      );
      const toStr = format(
        endOf(to, "month", { timeZone: currentTimeZone() }),
        DateFormat.Date46Elks,
      );
      return `/companies/${id}/usage${buildParams({
        from: fromStr,
        to: toStr,
      })}`;
    },
  ),
};

const DATA_EXPORT_ROUTES = {
  companyExports: route.fromTemplate("/admin/companies/:id/exports", {
    params: z.object({ id: z.string() }),
  }),
  exportBookings: route.fromTemplate(
    "/admin/companies/:id/exports/bookings.xlsx",
    {
      params: z.object({ id: z.string() }),
      query: z.object({
        locale: z.enum(LOCALES).catch(i18n.fallbackLng),
        from: z.string().optional(),
        to: z.string().optional(),
      }),
    },
  ),
  exportCustomerAnimals: route.fromTemplate(
    "/admin/companies/:id/exports/customer-animals.xlsx",
    {
      params: z.object({ id: z.string() }),
      query: z.object({ locale: z.enum(LOCALES).catch(i18n.fallbackLng) }),
    },
  ),
  exportCustomers: route.fromTemplate(
    "/admin/companies/:id/exports/customers.xlsx",
    {
      params: z.object({ id: z.string() }),
      query: z.object({ locale: z.enum(LOCALES).catch(i18n.fallbackLng) }),
    },
  ),
  exportServices: route.fromTemplate(
    "/admin/companies/:id/exports/services.xlsx",
    {
      params: z.object({ id: z.string() }),
      query: z.object({ locale: z.enum(LOCALES).catch(i18n.fallbackLng) }),
    },
  ),
};

const SYSTEM_MESSAGES_ROUTES = {
  systemMessages: route.fromTemplate("/admin/system-messages"),
  editSystemMessage: route.fromTemplate(`/admin/system-messages/:id/edit`, {
    params: z.object({ id: z.string() }),
  }),
  newSystemMessage: route("/admin/system-messages/new"),
};

const STATISTICS = {
  statistics: route.fromTemplate("/admin/statistics"),
};

const SURVEY_ROUTES = {
  surveys: route.fromTemplate("/admin/surveys"),
  newSurvey: route.fromTemplate("/admin/surveys/new"),
  newSurveyVersion: route.fromTemplate("/admin/surveys/:id/versions/new", {
    params: z.object({ id: z.string() }),
  }),
  previewSurvey: route.fromTemplate("/admin/surveys/:id/preview", {
    params: z.object({ id: z.string() }),
  }),
  surveySubmissions: route.fromTemplate("/admin/surveys/:id/submissions", {
    params: z.object({ id: z.string() }),
  }),
};

const USER_ROUTES = {
  users: route(
    ({ page = 1, query }: { page?: number; query?: string } = {}) =>
      `/admin/users${buildParams({ page, query })}`,
  ),
  viewUser: route.fromTemplate("/admin/users/:id", {
    params: z.object({ id: z.string() }),
  }),
  resetUserPassword: route.fromTemplate("/admin/users/:id/reset-password", {
    params: z.object({ id: z.string() }),
  }),
  deleteUser: route.fromTemplate("/admin/users/:id/delete", {
    params: z.object({ id: z.string() }),
  }),
};

const ADMIN_ROUTES = {
  admin: {
    ...REVIEW_IMPORT_ROUTES,
    ...ANALYTICS_ROUTES,
    ...SUBSCRIPTION_ROUTES,
    ...INVOICE_ROUTES,
    ...JOB_ROUTES,
    ...COMPANY_ROUTES,
    ...DATA_EXPORT_ROUTES,
    ...SYSTEM_MESSAGES_ROUTES,
    ...STATISTICS,
    ...SURVEY_ROUTES,
    ...USER_ROUTES,
    testSMS: route.fromTemplate("/test/sms"),
    companyImporter: route(
      ({ importType, id }: { id: string; importType: ImportType }) =>
        `/admin/companies/${id}/importers/${importType}`,
    ),
    home: route("/admin/home"),
    sendEmails: route("/admin/send-emails"),
    editFeatureFlags: route(
      ({ id }: Partial<Pick<Company, "id">> = {}) =>
        `/feature-flags/edit${buildParams({ companyId: id })}`,
    ),
    testEmail: route((id: string) => `/test/emails/${id}`),
    testEmails: route(`/test/emails`),
    testUISystem: route("/test/ui-system"),
  },
};

export default ADMIN_ROUTES;
